import { useEffect, useState } from 'react';
import { apiGet, apiPost } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line, ComposedChart,
} from 'recharts';
import { Zap, Cpu, HardDrive, MemoryStick, TrendingDown, Sparkles, RefreshCw, Trash2, UserCheck, Crown } from 'lucide-react';
import { toast } from 'sonner';

const COLORS = ['#6366F1', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#14B8A6', '#EC4899', '#64748B'];

function fmt(n) {
  if (n === null || n === undefined) return '0';
  if (n >= 100000) return `${(n / 100000).toFixed(2)}L`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

function MetricCard({ icon: Icon, label, value, sub, color = 'indigo', testid }) {
  const tones = {
    indigo: 'from-indigo-500 to-blue-600',
    emerald: 'from-emerald-500 to-teal-600',
    amber: 'from-amber-500 to-orange-500',
    rose: 'from-rose-500 to-pink-600',
    slate: 'from-slate-500 to-slate-700',
    violet: 'from-violet-500 to-purple-600',
  };
  return (
    <div className="rounded-2xl bg-white border border-slate-100 p-5 relative overflow-hidden" data-testid={testid}>
      <div className={`absolute top-0 right-0 w-24 h-24 rounded-full bg-gradient-to-br ${tones[color]} opacity-10 blur-2xl pointer-events-none`} />
      <div className="flex items-center gap-2 mb-1.5">
        <div className={`w-8 h-8 rounded-xl bg-gradient-to-br ${tones[color]} flex items-center justify-center text-white`}>
          <Icon className="w-4 h-4" />
        </div>
        <p className="text-[11px] font-heading uppercase tracking-wider text-slate-500">{label}</p>
      </div>
      <p className="font-display text-2xl font-bold text-slate-900 tracking-tight" data-testid={`${testid}-value`}>{value}</p>
      {sub && <p className="text-[11px] text-slate-400 font-body mt-0.5">{sub}</p>}
    </div>
  );
}

export default function CostDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (d = days) => {
    setRefreshing(true);
    try {
      const r = await apiGet(`/admin/usage/overview?days=${d}`);
      setData(r);
    } catch (e) { toast.error('Failed to load usage data'); }
    setLoading(false); setRefreshing(false);
  };

  useEffect(() => { load(days); /* eslint-disable-next-line */ }, [days]);

  const flushCache = async () => {
    if (!window.confirm('Flush the ENTIRE chat cache? Students will hit the LLM on every question until it warms up again.')) return;
    try {
      const r = await apiPost('/admin/cache/invalidate?flush_all=true', {});
      toast.success(`Cache flushed — ${r.deleted} entries removed`);
      load(days);
    } catch { toast.error('Failed to flush cache'); }
  };

  if (loading || !data) {
    return <div className="h-64 flex items-center justify-center"><div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>;
  }

  const infra = data.infra || {};
  const totalCost = Number(data.total_cost_inr || 0);
  const saved = Number(data.cache_cost_saved_estimate_inr || 0);
  const hitRate = Number(data.cache_hit_rate_pct || 0);

  const byTypePie = (data.by_type || []).filter(t => t.cost_inr > 0).map(t => ({ name: t.call_type, value: Number(t.cost_inr.toFixed(2)) }));
  const byDay = (data.by_day || []).map(d => ({ day: d.day.slice(5), cost: Number(d.cost_inr || 0), calls: d.calls, cached: d.cached }));

  return (
    <div className="space-y-6" data-testid="cost-dashboard">
      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-xl font-bold text-slate-900 tracking-tight">Cost, Usage &amp; Infrastructure</h3>
          <p className="text-xs text-slate-500 font-body mt-1">LLM spend, cache effectiveness, and server health — all in one view.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-full bg-white border border-slate-200 p-0.5">
            {[7, 14, 30, 90].map(d => (
              <button key={d} data-testid={`cost-range-${d}`} onClick={() => setDays(d)}
                className={`px-3 py-1 text-xs font-heading rounded-full transition-all ${days === d ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-800'}`}>
                {d}d
              </button>
            ))}
          </div>
          <Button data-testid="cost-refresh" onClick={() => load(days)} disabled={refreshing}
            className="bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-full font-heading text-xs">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Primary metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard icon={Zap} label={`Spend · last ${days}d`} value={`₹${totalCost.toFixed(2)}`} sub={`${fmt(data.total_calls)} API calls`} color="indigo" testid="metric-spend" />
        <MetricCard icon={Sparkles} label="Cache hit rate" value={`${hitRate}%`} sub={`${fmt(data.cached_calls)} of ${fmt(data.total_calls)} cached`} color="emerald" testid="metric-hit-rate" />
        <MetricCard icon={TrendingDown} label="Cache savings" value={`₹${saved.toFixed(2)}`} sub={`${fmt(data.cache_entries)} entries cached`} color="amber" testid="metric-savings" />
        <MetricCard icon={HardDrive} label="Storage used" value={`${infra.disk_used_gb || 0} GB`} sub={`${infra.disk_percent || 0}% of ${infra.disk_total_gb || 0} GB`} color="violet" testid="metric-storage" />
      </div>

      {/* Infra health */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <InfraBar icon={Cpu} label="CPU" pct={infra.cpu_percent || 0} detail={`${infra.cpu_percent || 0}% used`} testid="infra-cpu" />
        <InfraBar icon={MemoryStick} label="Memory" pct={infra.memory_percent || 0} detail={`${(infra.memory_used_mb / 1024 || 0).toFixed(1)} / ${(infra.memory_total_mb / 1024 || 0).toFixed(1)} GB`} testid="infra-memory" />
        <InfraBar icon={HardDrive} label="Uploads" pct={(infra.uploads_size_mb || 0) > 0 ? Math.min(100, (infra.uploads_size_mb / 1024 / (infra.disk_total_gb || 1)) * 100) : 0} detail={`${((infra.uploads_size_mb || 0) / 1024).toFixed(2)} GB of syllabus & images`} testid="infra-uploads" />
      </div>

      {/* Spend over time + by type */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 rounded-2xl border-slate-100 col-span-2" data-testid="chart-spend-timeline">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-heading text-sm font-semibold text-slate-800">Daily spend (INR)</h4>
            <span className="text-[10px] text-slate-400 font-body uppercase tracking-wider">Last {days}d</span>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={byDay}>
                <defs>
                  <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366F1" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#6366F1" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                <XAxis dataKey="day" stroke="#94A3B8" fontSize={11} />
                <YAxis stroke="#94A3B8" fontSize={11} tickFormatter={v => `₹${v}`} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0', fontSize: 12 }}
                  formatter={(v, name) => name === 'cost' ? [`₹${Number(v).toFixed(2)}`, 'Cost'] : [v, name]}
                />
                <Area type="monotone" dataKey="cost" stroke="#6366F1" strokeWidth={2} fill="url(#spendGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 rounded-2xl border-slate-100" data-testid="chart-cost-by-type">
          <h4 className="font-heading text-sm font-semibold text-slate-800 mb-3">Cost split · by operation</h4>
          {byTypePie.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-slate-400 text-xs">No spend yet in window</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={byTypePie} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={85} paddingAngle={2}>
                    {byTypePie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={v => `₹${Number(v).toFixed(2)}`} contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0', fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 10 }} iconSize={8} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>

      {/* Calls by type bar */}
      <Card className="p-5 rounded-2xl border-slate-100" data-testid="chart-calls-by-type">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-heading text-sm font-semibold text-slate-800">API calls · by operation</h4>
          <span className="text-[10px] text-slate-400 font-body uppercase tracking-wider">cached vs. fresh</span>
        </div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.by_type || []}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
              <XAxis dataKey="call_type" stroke="#94A3B8" fontSize={10} interval={0} angle={-18} textAnchor="end" height={60} />
              <YAxis stroke="#94A3B8" fontSize={11} />
              <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0', fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
              <Bar dataKey="calls" fill="#6366F1" name="Total calls" radius={[6, 6, 0, 0]} />
              <Bar dataKey="cached" fill="#10B981" name="Cached hits" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Cost per Active User — unit economics at a glance */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 rounded-2xl border-slate-100 col-span-2" data-testid="chart-cost-per-user">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <h4 className="font-heading text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                <UserCheck className="w-4 h-4 text-indigo-500" /> Cost per active user
              </h4>
              <p className="text-[11px] text-slate-400 font-body mt-0.5">Daily active users (bars) vs ₹ per active user (line) — your unit economics at a glance.</p>
            </div>
            <div className="flex items-center gap-3 text-[10px] font-heading uppercase tracking-wider text-slate-500">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-indigo-400" /> Active users</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-0.5 bg-emerald-500" /> ₹ per user</span>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={(data.per_user?.series || []).map(s => ({ ...s, day: s.day.slice(5) }))}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                <XAxis dataKey="day" stroke="#94A3B8" fontSize={11} />
                <YAxis yAxisId="left" stroke="#94A3B8" fontSize={11} allowDecimals={false} />
                <YAxis yAxisId="right" orientation="right" stroke="#10B981" fontSize={11} tickFormatter={v => `₹${v}`} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0', fontSize: 12 }}
                  formatter={(v, name) => {
                    if (name === 'cost_per_user') return [`₹${Number(v).toFixed(2)}`, 'Cost / active user'];
                    if (name === 'active_users') return [v, 'Active users'];
                    if (name === 'total_cost') return [`₹${Number(v).toFixed(2)}`, 'Total cost'];
                    return [v, name];
                  }}
                />
                <Bar yAxisId="left" dataKey="active_users" fill="#6366F1" radius={[6, 6, 0, 0]} name="active_users" />
                <Line yAxisId="right" type="monotone" dataKey="cost_per_user" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3, fill: '#10B981' }} name="cost_per_user" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 rounded-2xl border-slate-100 flex flex-col" data-testid="per-user-summary">
          <h4 className="font-heading text-sm font-semibold text-slate-800 mb-3">Window summary</h4>
          <div className="space-y-3">
            <div className="p-3 rounded-xl bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-100">
              <p className="text-[10px] font-heading uppercase tracking-wider text-indigo-600">Unique active users</p>
              <p className="font-display text-3xl font-bold text-slate-900 mt-1" data-testid="per-user-dau-total">{data.per_user?.unique_active_users || 0}</p>
              <p className="text-[10px] text-slate-400 font-body mt-0.5">in the last {days} days</p>
            </div>
            <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100">
              <p className="text-[10px] font-heading uppercase tracking-wider text-emerald-600">Avg. ₹ per active user</p>
              <p className="font-display text-3xl font-bold text-slate-900 mt-1" data-testid="per-user-avg-cost">₹{Number(data.per_user?.avg_cost_per_user_inr || 0).toFixed(2)}</p>
              <p className="text-[10px] text-slate-400 font-body mt-0.5">
                total spend ÷ unique users
              </p>
            </div>
            <div className="p-3 rounded-xl bg-slate-50/60 border border-slate-100">
              <p className="text-[10px] font-heading uppercase tracking-wider text-slate-500">Monthly projection</p>
              <p className="font-display text-xl font-bold text-slate-900 mt-1" data-testid="per-user-month-proj">
                ₹{(() => {
                  const v = Number(data.per_user?.avg_cost_per_user_inr || 0);
                  // Scale from window → 30 days
                  const projected = days > 0 ? (v * 30 / days) : 0;
                  return projected.toFixed(2);
                })()}
              </p>
              <p className="text-[10px] text-slate-400 font-body mt-0.5">per active user / 30 days</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Top spenders table */}
      <Card className="p-5 rounded-2xl border-slate-100" data-testid="top-spenders-card">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h4 className="font-heading text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <Crown className="w-4 h-4 text-amber-500" /> Top spenders
          </h4>
          <span className="text-[10px] text-slate-400 font-body uppercase tracking-wider">Users with highest LLM / image cost in the last {days}d</span>
        </div>
        {(data.per_user?.top_spenders || []).length === 0 ? (
          <p className="text-xs text-slate-400 font-body italic py-6 text-center">No user spend recorded in this window yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-100">
            <table className="w-full text-xs" data-testid="top-spenders-table">
              <thead className="bg-slate-50/60 text-slate-500 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">User</th>
                  <th className="px-3 py-2 text-left">Tier</th>
                  <th className="px-3 py-2 text-right">Calls</th>
                  <th className="px-3 py-2 text-right">Cost</th>
                </tr>
              </thead>
              <tbody>
                {data.per_user.top_spenders.map((s, i) => (
                  <tr key={s.user_id} className="border-t border-slate-100" data-testid={`top-spender-row-${i}`}>
                    <td className="px-3 py-2 text-slate-400 font-heading">{i + 1}</td>
                    <td className="px-3 py-2">
                      <p className="font-heading text-slate-800">{s.name}</p>
                      {s.email && <p className="text-[10px] text-slate-400 font-body">{s.email}</p>}
                    </td>
                    <td className="px-3 py-2">
                      <Badge className={`rounded-full text-[10px] capitalize ${
                        s.tier === 'premium' || s.tier === 'elite' ? 'bg-amber-100 text-amber-700' :
                        s.tier === 'growth' ? 'bg-indigo-100 text-indigo-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>{s.tier || '—'}</Badge>
                    </td>
                    <td className="px-3 py-2 text-right text-slate-600 font-body">{s.calls}</td>
                    <td className="px-3 py-2 text-right font-heading text-slate-900">₹{Number(s.cost_inr).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Top cached questions + controls */}
      <Card className="p-5 rounded-2xl border-slate-100" data-testid="cache-top-questions">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h4 className="font-heading text-sm font-semibold text-slate-800">Top cached questions</h4>
          <Button data-testid="cache-flush-all" onClick={flushCache}
            className="bg-rose-500 hover:bg-rose-600 text-white rounded-full font-heading text-xs">
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Flush entire cache
          </Button>
        </div>
        {(data.top_cached_questions || []).length === 0 ? (
          <p className="text-xs text-slate-400 font-body italic py-6 text-center">Cache is empty — it warms as students ask questions.</p>
        ) : (
          <div className="space-y-2">
            {data.top_cached_questions.map((q, i) => (
              <div key={i} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-slate-50/60 border border-slate-100" data-testid={`cache-top-row-${i}`}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-blue-600 text-white font-heading text-xs flex items-center justify-center flex-shrink-0">{i + 1}</span>
                  <div className="min-w-0">
                    <p className="text-sm text-slate-800 font-body truncate">{q.question}</p>
                    <p className="text-[10px] text-slate-400 font-body uppercase tracking-wider mt-0.5">{q.subject} · {q.topic}</p>
                  </div>
                </div>
                <Badge className="bg-emerald-100 text-emerald-700 rounded-full font-heading text-[10px]">{q.hits} hit{q.hits === 1 ? '' : 's'}</Badge>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Cost model (reference table) */}
      <Card className="p-5 rounded-2xl border-slate-100" data-testid="cost-model-ref">
        <h4 className="font-heading text-sm font-semibold text-slate-800 mb-3">Cost model · per API call (INR)</h4>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {Object.entries(data.cost_model || {}).map(([k, v]) => (
            <div key={k} className="p-3 rounded-xl bg-slate-50/60 border border-slate-100 text-xs" data-testid={`cost-row-${k}`}>
              <p className="font-heading text-slate-500 text-[10px] uppercase tracking-wider truncate">{k}</p>
              <p className="font-display text-base font-bold text-slate-900 mt-1">₹{Number(v).toFixed(2)}</p>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-slate-400 font-body italic mt-3">These are blended averages per call. Actual costs depend on tokens used — adjust in <code className="bg-slate-100 px-1 rounded">backend/services/cost_cache.py</code> if pricing shifts.</p>
      </Card>
    </div>
  );
}

function InfraBar({ icon: Icon, label, pct, detail, testid }) {
  const color = pct > 85 ? 'bg-rose-500' : pct > 60 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="rounded-2xl bg-white border border-slate-100 p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-slate-500" />
        <p className="font-heading text-sm font-semibold text-slate-800">{label}</p>
        <span className="ml-auto font-display text-sm font-bold text-slate-900">{Number(pct).toFixed(1)}%</span>
      </div>
      <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <p className="text-[11px] text-slate-400 font-body mt-2">{detail}</p>
    </div>
  );
}
